import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { Paperclip, Send } from 'lucide-react'
import { fehlertext } from '@/lib/fehlertext'
import { EigeneDateien } from '@/components/mail/EigeneDateien'
import { versand, type SendDraft } from './versand'

const eingabe =
  'w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring'

type Weg = 'MICROSOFT_365' | 'SYSTEM'

interface Props<T> {
  open: boolean
  beleg: { id: string; label: string }
  onClose: () => void
  /** Der Beleg nach dem Versand (bzw. nach einem Fehlschlag neu gelesen) — in der Form des Belegwesens. */
  onSent: (b: T) => void
  /** AP-37 Detailblatt O 6.4: „Preisanfrage 2 von 3 — Stahl Maier" beim Versand nacheinander. */
  kopfzeile?: string
  /** Versand nacheinander: diesen Beleg auslassen (bleibt im Entwurf). */
  onSkip?: () => void
  /** Nur nach erfolgreichem Versand, vor `onClose` — Abbrechen ruft es nicht. */
  onGesendet?: () => void
}

/**
 * AP-32 14.1 — **Beleg per E-Mail.** Empfänger vorbelegt aus dem Ansprechpartner
 * mit passender Rolle, Betreff und Text aus den Textbausteinen, das PDF im
 * Anhang. Ein Fehlschlag wird am Beleg festgehalten und hier gezeigt.
 *
 * AP-10 Nachtrag 1, 13.3: „Von" — eigenes Microsoft-365-Postfach oder Symphenia.
 * Welche Wege es gibt, sagt der Server; er prüft beim Senden erneut.
 *
 * AP-32 Nachtrag 10: eigene Dateien vom Rechner — nur für diese Mail, immer als Anhang.
 *
 * AP-37 Detailblatt O 6.4 (O-14): liegt unter `components/belege/`, weil auch das
 * Cockpit „Materialanfragen" damit sendet — ein Versandweg, dieselben Regeln.
 */
export function SendDialog<T>({ open, beleg, onClose, onSent, kopfzeile, onSkip, onGesendet }: Props<T>) {
  const { t } = useTranslation()
  const [vorlage, setVorlage] = useState<SendDraft | null>(null)
  const [an, setAn] = useState('')
  const [kopie, setKopie] = useState(false)
  const [betreff, setBetreff] = useState('')
  const [nachricht, setNachricht] = useState('')
  const [weg, setWeg] = useState<Weg | null>(null)
  const [dateien, setDateien] = useState<File[]>([])
  const [herkunft, setHerkunft] = useState<string[]>([])
  const [fehler, setFehler] = useState<string | null>(null)
  const [laeuft, setLaeuft] = useState(false)

  const ladeVorlage = (vollstaendig: boolean) =>
    versand
      .vorlage(beleg.id)
      .then(({ data }) => {
        setVorlage(data.data)
        setWeg(data.data.defaultTransport ?? (data.data.transports ? null : 'SYSTEM'))
        if (!vollstaendig) return
        setAn(data.data.to.join(', '))
        setBetreff(data.data.subject)
        setNachricht(data.data.message)
        setKopie(false)
        // AP-27 7. (RB-19): die Regieberichte der Zeilen gehen vorgewählt mit.
        setHerkunft((data.data.sourceAttachments ?? []).map((a) => a.key))
      })
      .catch((err) => setFehler(fehlertext(err, t)))

  useEffect(() => {
    if (!open) return
    setFehler(null)
    setVorlage(null)
    setDateien([])
    void ladeVorlage(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, beleg.id])

  if (!open) return null

  const adressen = an
    .split(/[,;\s]+/)
    .map((a) => a.trim())
    .filter(Boolean)

  const hinzufuegen = (email: string) => {
    if (adressen.some((a) => a.toLowerCase() === email.toLowerCase())) return
    setAn([...adressen, email].join(', '))
  }

  const ms = vorlage?.transports?.microsoft365 ?? null
  const system = vorlage?.transports?.system ?? null
  const ueberMicrosoft = weg === 'MICROSOFT_365'

  const senden = async () => {
    setFehler(null)
    setLaeuft(true)
    try {
      const { data } = await versand.senden<T>(beleg.id, {
        to: adressen,
        copyToSelf: kopie && !ueberMicrosoft,
        subject: betreff,
        message: nachricht,
        ...(weg ? { transport: weg } : {}),
        ...(herkunft.length ? { sourceAttachments: herkunft } : {}),
      }, dateien)
      onSent(data.data)
      onGesendet?.()
      onClose()
    } catch (err) {
      setFehler(fehlertext(err, t))
      // Abgelaufene Verbindung: Wege neu laden, damit die Auswahl stimmt — die Eingaben bleiben.
      const code = (err as { response?: { data?: { code?: string } } })?.response?.data?.code
      if (code === 'MAIL_CONNECTION_EXPIRED') void ladeVorlage(false)
      // Der Fehlschlag steht jetzt im Verlauf des Belegs — neu laden, damit er sichtbar ist.
      versand
        .lesen<T>(beleg.id)
        .then(({ data }) => onSent(data.data))
        .catch(() => undefined)
    } finally {
      setLaeuft(false)
    }
  }

  const lieferung = (a: NonNullable<SendDraft['attachments']>[number]) => (ueberMicrosoft ? (a.deliveryMicrosoft365 ?? a.delivery) : a.delivery)

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div
        className="flex max-h-[90vh] w-full max-w-2xl flex-col rounded-lg border border-border shadow-xl"
        style={{ background: 'hsl(var(--card))', color: 'hsl(var(--card-foreground))' }}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className="border-b border-border p-4">
          {kopfzeile && <p className="text-xs font-medium uppercase tracking-wide text-primary">{kopfzeile}</p>}
          <h2 className="text-lg font-semibold">{t('documents.send.title')}</h2>
          {vorlage && !ueberMicrosoft && <p className="text-xs text-muted-foreground">{t('documents.send.replyTo', { email: vorlage.replyTo })}</p>}
        </div>
        <div className="flex-1 space-y-3 overflow-auto p-4">
          {vorlage?.draftLetter && <p className="rounded-md bg-amber-100 px-3 py-2 text-xs text-amber-900 dark:bg-amber-900/40 dark:text-amber-100">{t('documents.send.draftLetter')}</p>}

          {/* AP-10 Nachtrag 1: Von */}
          {vorlage?.transports && (ms || !system) && (
            <div className="space-y-1">
              <span className="block text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t('documents.send.from')}</span>
              {ms && (
                <label className="flex items-center gap-2 text-sm">
                  <input type="radio" name="sendTransport" disabled={ms.status !== 'OK'} checked={weg === 'MICROSOFT_365'} onChange={() => setWeg('MICROSOFT_365')} />
                  {t('documents.send.fromMicrosoft', { address: ms.address ?? '' })}
                  {ms.status === 'EXPIRED' && <span className="rounded bg-amber-100 px-1.5 py-0.5 text-xs text-amber-900 dark:bg-amber-900/40 dark:text-amber-100">{t('mailConnection.status.EXPIRED')}</span>}
                </label>
              )}
              {system && (
                <label className="flex items-center gap-2 text-sm">
                  <input type="radio" name="sendTransport" checked={weg === 'SYSTEM'} onChange={() => setWeg('SYSTEM')} />
                  {t('documents.send.fromSystem', { address: system.address })}
                </label>
              )}
              {(ms?.status === 'EXPIRED' || (!ms && !system)) && (
                <p className="text-xs text-muted-foreground">
                  {t(ms?.status === 'EXPIRED' ? 'documents.send.expiredHint' : 'documents.send.connectRequired')}{' '}
                  <Link to="/profile" className="text-primary hover:underline">
                    {t('documents.send.toProfile')}
                  </Link>
                </p>
              )}
            </div>
          )}
          {vorlage?.connectHint && !ms && system && (
            <p className="text-xs text-muted-foreground">
              {t('documents.send.connectHint')}{' '}
              <Link to="/profile" className="text-primary hover:underline">
                {t('documents.send.toProfile')}
              </Link>
            </p>
          )}

          <label className="block space-y-1">
            <span className="block text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t('documents.send.to')}</span>
            <input className={eingabe} value={an} onChange={(e) => setAn(e.target.value)} placeholder="name@example.com" />
            <span className="block text-xs text-muted-foreground">{t('documents.send.toHint')}</span>
          </label>
          {vorlage && vorlage.contacts.length > 0 && (
            <div className="flex flex-wrap items-center gap-1.5 text-xs">
              <span className="text-muted-foreground">{t('documents.send.contacts')}:</span>
              {vorlage.contacts.map((k) => (
                <button key={k.email} type="button" onClick={() => hinzufuegen(k.email)} className="rounded-full border border-border px-2 py-0.5 hover:bg-muted">
                  {k.name ? `${k.name} · ${k.email}` : k.email}
                </button>
              ))}
            </div>
          )}
          {/* Aus dem eigenen Postfach liegt die Mail ohnehin in „Gesendete Elemente". */}
          {ueberMicrosoft ? (
            <p className="text-xs text-muted-foreground">{t('documents.send.sentItemsHint')}</p>
          ) : (
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={kopie} onChange={(e) => setKopie(e.target.checked)} />
              {t('documents.send.copyToSelf')}
            </label>
          )}
          <label className="block space-y-1">
            <span className="block text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t('documents.send.subject')}</span>
            <input className={eingabe} value={betreff} maxLength={250} onChange={(e) => setBetreff(e.target.value)} />
          </label>
          <label className="block space-y-1">
            <span className="block text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t('documents.send.message')}</span>
            <textarea className={eingabe} rows={8} value={nachricht} maxLength={10000} onChange={(e) => setNachricht(e.target.value)} />
          </label>
          <div className="space-y-0.5 text-xs text-muted-foreground">
            <p className="flex items-center gap-1.5">
              <Paperclip className="h-3.5 w-3.5" />
              {t('documents.send.attachment')}: {beleg.label}.pdf
            </p>
            {/* Nachtrag 4: Dateien an Positionen gehen mit — AP-10 Nachtrag 2: als Anhang oder als Link. */}
            {(vorlage?.attachments ?? []).map((a, i) => (
              <p key={`${a.fileName}-${i}`} className="pl-5">
                {a.fileName} ({a.sizeBytes >= 1024 * 1024 ? `${(a.sizeBytes / (1024 * 1024)).toFixed(1)} MB` : `${Math.max(1, Math.round(a.sizeBytes / 1024))} KB`})
                {lieferung(a) === 'LINK' && vorlage?.linkValidUntil && (
                  <span className="ml-1.5 rounded bg-sky-100 px-1.5 py-0.5 text-sky-900 dark:bg-sky-900/40 dark:text-sky-100">
                    {t('documents.send.asLink', { date: new Date(`${vorlage.linkValidUntil}T12:00:00`).toLocaleDateString(navigator.language) })}
                  </span>
                )}
              </p>
            ))}
            {(vorlage?.attachments ?? []).some((a) => lieferung(a) === 'LINK') && (
              <p className="pt-1">{t(vorlage?.linkAsZip ? 'documents.send.linkNoteZip' : 'documents.send.linkNote')}</p>
            )}
            {ueberMicrosoft && (vorlage?.attachments ?? []).some((a) => a.deliveryMicrosoft365 === 'LINK' && a.delivery !== 'LINK') && (
              <p>{t('documents.send.microsoftLinkNote')}</p>
            )}
          </div>
          {/* AP-27 7. (RB-19): Dateien aus der Herkunft der Zeilen — vorgewählt, abwählbar, immer Anhang. */}
          {(vorlage?.sourceAttachments ?? []).length > 0 && (
            <div className="space-y-1 rounded-md border border-border p-3">
              <span className="block text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t('documents.send.sourceAttachments')}</span>
              {(vorlage?.sourceAttachments ?? []).map((a) => (
                <label key={a.key} className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={herkunft.includes(a.key)}
                    onChange={(e) => setHerkunft((alt) => (e.target.checked ? [...alt, a.key] : alt.filter((k) => k !== a.key)))}
                  />
                  <Paperclip className="h-3.5 w-3.5 text-muted-foreground" />
                  {a.label}
                </label>
              ))}
            </div>
          )}
          {/* Nachtrag 10: eigene Dateien vom Rechner — gehen nur mit dieser Mail, am Beleg bleibt nichts. */}
          <div className="space-y-1 rounded-md border border-border p-3">
            <EigeneDateien dateien={dateien} onChange={setDateien} ueberMicrosoft={ueberMicrosoft} />
            {dateien.length > 0 && <p className="text-xs text-muted-foreground">{t('documents.send.ownFilesNote')}</p>}
          </div>
          {fehler && <div className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">{fehler}</div>}
        </div>
        <div className="flex justify-end gap-2 border-t border-border p-4">
          <button type="button" onClick={onClose} className="rounded-md border border-border bg-background px-3 py-2 text-sm hover:bg-card">
            {t('common.cancel')}
          </button>
          {onSkip && (
            <button type="button" onClick={onSkip} className="rounded-md border border-border bg-background px-3 py-2 text-sm hover:bg-card">
              Überspringen
            </button>
          )}
          <button
            type="button"
            disabled={laeuft || !vorlage || !weg || adressen.length === 0 || !betreff.trim()}
            onClick={() => void senden()}
            className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground disabled:opacity-50"
          >
            <Send className="h-4 w-4" />
            {t('documents.send.submit')}
          </button>
        </div>
      </div>
    </div>
  )
}
